import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Sparkle, CircleNotch, Plus, Trash, Check, Copy, ArrowSquareOut, DiamondsFour as Gem, Storefront, MapPin, CurrencyDollar, Stack, Cube, ArrowArcLeft, ArrowArcRight, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Model3DParts from "./Model3DParts";
import PaletteEditor from "@/components/PaletteEditor";
import ZoneLogoRules from "@/components/ZoneLogoRules";
import { ENGRAVING_FONTS } from "@/lib/fonts";
import { MATERIAL_FAMILIES, FAMILY_ORDER, MATERIAL_PRESETS, CLASS_DEFAULT_FAMILY, FAMILY_SIZE, SIZE_UNITS } from "@/lib/materials";

const MODES = [
  { v: "3d", l: "3D (visor interactivo)" },
];

const METALS = MATERIAL_PRESETS.metal;
const ZONE_TYPES = [
  { v: "logo", l: "Logo" }, { v: "engraving", l: "Grabado" },
  { v: "color", l: "Color metal" }, { v: "stone", l: "Piedra" },
];
const PRODUCT_CLASSES = [
  { key: "joyeria", label: "Joyería" }, { key: "ropa", label: "Ropa" },
  { key: "accesorio", label: "Accesorio" }, { key: "mueble", label: "Mueble" },
  { key: "modelo3d", label: "Modelo 3D" }, { key: "ilustracion", label: "Ilustración" },
  { key: "otro", label: "Otro" },
];
const JEWELRY_SUBTYPES = [
  { key: "anillo_compromiso", label: "Anillo de compromiso" }, { key: "sortija_graduacion", label: "Sortija de graduación" },
  { key: "cadena", label: "Cadena" }, { key: "reloj", label: "Reloj" }, { key: "aretes", label: "Aretes" },
  { key: "pulsera", label: "Pulsera" }, { key: "dije", label: "Dije" }, { key: "otro", label: "Otro" },
];

export default function ProductDetail() {
  const { t } = useTranslation();
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [storeSlug, setStoreSlug] = useState("");
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);  const posRef = useRef(null);
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#888888");
  const partsTimer = useRef(null);
  const hist = useRef({ past: [], future: [] });
  const lastParts = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [demoId, setDemoId] = useState(null);
  const [wixLinked, setWixLinked] = useState(false);

  const load = () => api.get(`/products/${pid}`).then((r) => setP(r.data));
  useEffect(() => { load().catch(() => toast.error(t("pd.t_notFound"))); api.get("/collections").then((r) => setCollections(r.data)).catch(() => {}); api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {}); api.get("/admin/demo-product").then((r) => setDemoId(r.data.demo_product_id)).catch(() => setDemoId(null)); api.get("/store/settings").then((r) => setStoreSlug(r.data.slug || "")).catch(() => {}); }, [pid]);
  const setAsDemo = async () => {
    try { const { data } = await api.put(`/admin/demo-product/${pid}`); setDemoId(data.demo_product_id); toast.success(t("pd.t_setDemo")); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (p) lastParts.current = p.model_parts || []; }, [p]);
  useEffect(() => { api.get("/wix/dashboard/status").then((r) => setWixLinked(!!r.data.linked)).catch(() => {}); }, []);

  const pushHistory = (prevParts) => {
    hist.current.past.push(JSON.parse(JSON.stringify(prevParts || [])));
    if (hist.current.past.length > 40) hist.current.past.shift();
    hist.current.future = [];
    setCanUndo(true); setCanRedo(false);
  };
  const undo = () => {
    const h = hist.current;
    if (!h.past.length) return;
    const prev = h.past.pop();
    h.future.push(JSON.parse(JSON.stringify(lastParts.current || [])));
    lastParts.current = prev;
    setP((s) => ({ ...s, model_parts: prev }));
    persistParts(prev);
    setCanUndo(h.past.length > 0); setCanRedo(true);
    toast.success(t("pd.t_undone"));
  };
  const redo = () => {
    const h = hist.current;
    if (!h.future.length) return;
    const next = h.future.pop();
    h.past.push(JSON.parse(JSON.stringify(lastParts.current || [])));
    lastParts.current = next;
    setP((s) => ({ ...s, model_parts: next }));
    persistParts(next);
    setCanRedo(h.future.length > 0); setCanUndo(true);
    toast.success(t("pd.t_redone"));
  };

  if (!p) return <div className="p-8"><CircleNotch className="animate-spin text-gold" size={28} /></div>;

  const moveMarker = (e) => {
    if (dragIdx == null || !posRef.current) return;
    const r = posRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    setP((prev) => ({ ...prev, zones: prev.zones.map((z, i) => i === dragIdx ? { ...z, position: { x, y } } : z) }));
  };

  const pushShopify = async () => {
    try { await api.post(`/shopify/push/${pid}`); toast.success(t("pd.t_shopifyPushed")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pushWix = async () => {
    try { const { data } = await api.post(`/integrations/wix/push/${pid}`); toast.success(t("pd.t_wixPushed") + (data.wix_product ? ` (${data.wix_product.slice(0, 8)}…)` : "")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pushWixConnected = async () => {
    try { await api.post(`/wix/dashboard/sync-product/${pid}`); toast.success("Producto enviado a tu tienda Wix conectada"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const embedUrl = `${window.location.origin}/embed/${p.id}`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="760" frameborder="0" style="border:0;border-radius:16px"></iframe>`;
  const copyText = (t2, label) => { try { navigator.clipboard.writeText(t2); toast.success(label || t("pd.copied")); } catch { toast.error(t("pd.t_copyFail")); } };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/products/${pid}/analyze`);
      setP(data);
      toast.success(t("pd.t_analyzed"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setAnalyzing(false); }
  };

  const describe = async () => {
    setDescribing(true);
    try {
      const { data } = await api.post(`/products/${pid}/describe`);
      setP((prev) => ({ ...prev, description: data.description }));
      toast.success(t("pd.t_described"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setDescribing(false); }
  };

  const autobuild = async () => {
    if (!window.confirm(t("pd.t_confirmAutobuild"))) return;
    setBuilding(true);
    try {
      pushHistory(lastParts.current);
      const { data } = await api.post(`/products/${pid}/autobuild`);
      setP(data.product);
      const s = data.summary;
      toast.success(`Armado: ${s.logos} logo(s), ${s.stones} piedra(s), ${s.enamel} esmalte(s). Ajusta las piezas en el editor.`);
      if (data.warnings?.length) toast.warning(data.warnings.join(" · "));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBuilding(false); }
  };

  const save = async (patch, silent) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/products/${pid}`, patch);
      if (silent) setP((prev) => ({ ...prev, ...patch }));
      else setP(data);
      if (!silent) toast.success(t("pd.t_saved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const persistParts = (mp) => {
    if (partsTimer.current) clearTimeout(partsTimer.current);
    partsTimer.current = setTimeout(() => {
      api.put(`/products/${pid}`, { model_parts: mp }).catch(() => {});
    }, 500);
  };
  const onPartsChange = (mp) => { pushHistory(lastParts.current); setP((prev) => ({ ...prev, model_parts: mp })); persistParts(mp); };

  const setField = (k, v) => setP({ ...p, [k]: v });

  const uploadRef = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "reference");
      const { data } = await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const refs = [...(p.reference_asset_ids || []), data.asset_id].slice(0, 5);
      await save({ reference_asset_ids: refs, base_asset_id: p.base_asset_id || data.asset_id }, true);
      toast.success(t("pd.t_imageUploaded"));
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeRef = async (aid) => {
    const refs = (p.reference_asset_ids || []).filter((x) => x !== aid);
    await save({ reference_asset_ids: refs, base_asset_id: p.base_asset_id === aid ? (refs[0] || null) : p.base_asset_id }, true);
  };

  const matFamily = () => p.material_family || CLASS_DEFAULT_FAMILY[p.klass || "joyeria"] || "metal";
  const toggleMetal = (m) => {
    const has = (p.metals || []).some((x) => x.id === m.id);
    const metals = has ? p.metals.filter((x) => x.id !== m.id) : [...(p.metals || []), { ...m, family: matFamily(), price: 0 }];
    setField("metals", metals);
  };
  const setMetalPrice = (id, price) => setField("metals", (p.metals || []).map((x) => x.id === id ? { ...x, price: parseFloat(price) || 0 } : x));
  const setMetalFactor = (id, factor) => setField("metals", (p.metals || []).map((x) => x.id === id ? { ...x, factor: parseFloat(factor) || 0 } : x));
  const setMetalMarket = (id, on) => setField("metals", (p.metals || []).map((x) => x.id === id ? { ...x, market: on } : x));
  const addCustomColor = () => {
    const name = (colorName || "").trim();
    if (!name) return;
    const id = "custom_" + Date.now().toString(36);
    setField("metals", [...(p.metals || []), { id, name, hex: colorHex, family: matFamily(), price: 0, custom: true }]);
    setColorName("");
  };
  const removeMaterial = (id) => setField("metals", (p.metals || []).filter((x) => x.id !== id));
  const setMaterialFamily = (fam) => { const patch = familyPatch(fam); setP({ ...p, ...patch }); save(patch, true); };
  // Reseed the customer-facing material options to the chosen family so the client only
  // ever sees options that match the product (a fabric shirt shows fabric colors, not gold).
  const seedMaterials = (fam, existing = []) => (MATERIAL_PRESETS[fam] || []).map((m) => {
    const prev = existing.find((x) => x.id === m.id);
    return { ...m, family: fam, price: prev?.price ?? 0, market: prev?.market ?? false, factor: prev?.factor ?? 0 };
  });
  // Patch applied whenever the main material changes: reseed materials, drop options that
  // don't make sense for the type (no engraving on clothing), and default the size unit.
  const familyPatch = (fam) => {
    const patch = { material_family: fam, metals: seedMaterials(fam, p.metals) };
    if (fam === "fabric") patch.engraving_enabled = false;
    const sz = FAMILY_SIZE[fam] || {};
    patch.size_unit = sz.unit || "";
    if (!(p.size_options || []).length && (sz.options || []).length) patch.size_options = sz.options;
    return patch;
  };

  const applyMarketPrices = async () => {
    if (!p.weight_grams) { toast.error(t("pd.t_weightFirst")); return; }
    try {
      const { data } = await api.get("/metal-prices");
      const spot = data.metal_spot_prices, type = data.metal_type;
      const metals = (p.metals || []).map((m) => ({ ...m, price: Math.round((p.weight_grams * (spot[type[m.id]] || 0)) * 100) / 100 }));
      setField("metals", metals);
      toast.success(t("pd.t_marketCalc"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateZone = (i, k, v) => {
    const zones = [...p.zones]; zones[i] = { ...zones[i], [k]: v }; setField("zones", zones);
  };
  const addZone = () => setField("zones", [...(p.zones || []), { id: "z" + Math.random().toString(36).slice(2, 8), name: "Nueva zona", type: "logo", description: "" }]);
  const delZone = (i) => setField("zones", p.zones.filter((_, j) => j !== i));

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true); toast.success(t("pd.t_codeCopied")); setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("pd.t_copyFail"));
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <Link to="/dashboard/products" className="inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-white mb-6"><ArrowLeft /> {t("pd.backToProducts")}</Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <Input
            value={p.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            onBlur={(e) => { const v = e.target.value.trim(); if (v) save({ name: v }, true).then(() => toast.success(t("pd.t_nameUpdated"))); }}
            className="text-3xl font-bold tracking-tight bg-transparent border-0 border-b border-transparent hover:border-white/20 focus:border-gold rounded-none px-0 h-auto py-1 focus-visible:ring-0"
            placeholder={t("pd.namePlaceholder")}
            data-testid="product-name-input"
          />
          <div className="flex items-center gap-2 mt-2">
            {p.product_type_label && <span className="text-xs px-2 py-0.5 rounded-full bg-gold/20 text-gold">{p.product_type_label}</span>}
            {p.has_stones && <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 flex items-center gap-1"><Gem size={12} /> {t("pd.withStones")}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {demoId !== null && (
            <Button onClick={setAsDemo} variant="outline" data-testid="set-demo-btn"
              className={`rounded-full ${demoId === p.id ? "border-gold text-gold" : "border-white/15"}`}>
              <Star size={14} weight={demoId === p.id ? "fill" : "regular"} className="mr-1" />
              {demoId === p.id ? t("pd.productOfDemo") : t("pd.useInDemo")}
            </Button>
          )}
          <a href={embedUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full border-white/15" data-testid="preview-customizer-btn"><ArrowSquareOut className="mr-1" /> {t("pd.clientView")}</Button></a>
          <Button onClick={() => save({ name: p.name, description: p.description, product_type_label: p.product_type_label, has_stones: p.has_stones, zones: p.zones, metals: p.metals, engraving_enabled: p.engraving_enabled, engraving_max_length: p.engraving_max_length, engraving_fonts: p.engraving_fonts, engraving_project_3d: p.engraving_project_3d, engraving_flip: p.engraving_flip, engraving_price: p.engraving_price, price: p.price, weight_grams: p.weight_grams, dwt: p.dwt, size_enabled: p.size_enabled, size_free_text: p.size_free_text, size_options: p.size_options, size_unit: p.size_unit, notes_enabled: p.notes_enabled, enamel_enabled: p.enamel_enabled, gold_plating_enabled: p.gold_plating_enabled, gold_plating_price: p.gold_plating_price, customer_upload_price: p.customer_upload_price, default_collection_id: p.default_collection_id, category_id: p.category_id, klass: p.klass, subtype: p.subtype, subcategory: p.subcategory, material_family: p.material_family, logo_customer_color: p.logo_customer_color, logo_color_mode: p.logo_color_mode, logo_default_color: p.logo_default_color, dimensions: p.dimensions, allowed_collection_ids: p.allowed_collection_ids, mode: p.mode, layers: p.layers, model_parts: p.model_parts, status: "published" })} disabled={saving} data-testid="save-product-btn" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold">
            {saving ? <CircleNotch className="animate-spin" /> : <><Check className="mr-1" /> {t("pd.saveAndPublish")}</>}
          </Button>
        </div>
      </div>

      <div className="border border-gold/25 rounded-xl p-5 bg-gradient-to-b from-gold/[0.06] to-transparent mb-6" data-testid="mode-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold mb-1 flex items-center gap-2"><Cube size={18} className="text-gold" /> {t("pd.editor3dTitle")}</h3>
            <p className="text-xs text-neutral-500">{t("pd.editor3dDesc")}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={undo} disabled={!canUndo} size="sm" variant="outline" className="rounded-full border-white/15 h-9" data-testid="undo-btn"><ArrowArcLeft size={15} className="mr-1" /> {t("pd.undo")}</Button>
            <Button onClick={redo} disabled={!canRedo} size="sm" variant="outline" className="rounded-full border-white/15 h-9" data-testid="redo-btn"><ArrowArcRight size={15} /></Button>
          </div>
        </div>
      </div>

      <CatalogTypeCard p={p} save={save} />

      <div className="mb-6 space-y-6">
        <Model3DParts pid={pid} parts={p.model_parts || []} onChange={onPartsChange} materialFamily={matFamily()}
          onUndo={undo} onRedo={redo} canUndo={canUndo} canRedo={canRedo} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: references + analyze */}
        <div className="space-y-6">
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">{t("pd.refImages")}</h3>
              <label className="text-xs text-gold cursor-pointer flex items-center gap-1" data-testid="upload-reference-label">
                <Plus size={13} /> {t("pd.upload")}
                <input type="file" accept="image/*" className="hidden" data-testid="upload-reference-input"
                  onChange={(e) => { uploadRef(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(p.reference_asset_ids || []).map((aid) => (
                <div key={aid} className="relative group">
                  <button onClick={() => save({ base_asset_id: aid }, true)} title="Marcar como foto principal (portada en Wix)" data-testid={`ref-photo-${aid}`}
                    className={`aspect-square w-full rounded-lg overflow-hidden border-2 ${p.base_asset_id === aid ? "border-gold" : "border-white/10"}`}>
                    <img src={assetUrl(`/api/assets/${aid}`)} alt="" className="w-full h-full object-cover" />
                  </button>
                  {p.base_asset_id === aid && (
                    <span className="absolute bottom-1 left-1 bg-gold text-obsidian text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid={`ref-cover-badge-${aid}`}><Star size={9} weight="fill" /> {t("pd.cover")}</span>
                  )}
                  <button onClick={() => removeRef(aid)} className="absolute -top-1 -right-1 bg-red-500/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100"><Trash size={11} /></button>
                </div>
              ))}
              {(p.reference_asset_ids || []).length === 0 && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-white/15 flex items-center justify-center text-neutral-500 cursor-pointer hover:border-gold/50 text-xs col-span-3 py-6" data-testid="upload-reference-empty">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadRef(e.target.files[0]); e.target.value = ""; }} />
                  <span className="flex items-center gap-1"><Plus size={14} /> {t("pd.refEmpty")}</span>
                </label>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-2">{t("pd.refNote")}</p>
          </div>

          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{t("pd.descTitle")}</h3>
              <Button onClick={describe} disabled={describing} size="sm" data-testid="describe-btn" className="rounded-full bg-white/10 hover:bg-white/20">
                {describing ? <CircleNotch className="animate-spin" /> : <><Sparkle className="mr-1" weight="fill" /> {t("pd.genWithAI")}</>}
              </Button>
            </div>
            <p className="text-xs text-neutral-500 mb-2">{t("pd.descNote")}</p>
            <Textarea value={p.description || ""} onChange={(e) => setField("description", e.target.value)} rows={6}
              placeholder={t("pd.descPlaceholder")} className="bg-white/5 border-white/15" data-testid="product-description-input" />
          </div>

          {/* Embed / share */}
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <h3 className="font-semibold mb-1">{t("pd.integrateTitle")}</h3>
            <p className="text-xs text-neutral-500 mb-3">{t("pd.integrateShort")}</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t("pd.clientLink")}</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={embedUrl} onFocus={(e) => e.target.select()} className="bg-white/5 border-white/15 font-mono text-xs h-9" data-testid="embed-code" />
                  <Button onClick={copyEmbed} size="sm" variant="outline" className="rounded-lg border-white/15 h-9 shrink-0" data-testid="copy-embed-btn">
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                  <a href={embedUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-lg border-white/15 h-9 shrink-0"><ArrowSquareOut size={14} /></Button></a>
                </div>
              </div>
              <div>
                <Label className="text-xs">{t("pd.customizerId")}</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={p.id} onFocus={(e) => e.target.select()} className="bg-white/5 border-white/15 font-mono text-xs h-9" data-testid="customizer-id" />
                  <Button onClick={() => copyText(p.id, t("pd.idCopied"))} size="sm" variant="outline" className="rounded-lg border-white/15 h-9 shrink-0" data-testid="copy-product-id-btn">
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">{t("pd.customizerIdNote")}</p>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={pushShopify} size="sm" variant="outline" className="rounded-full border-white/15" data-testid="push-shopify-btn">
                  <Storefront className="mr-1" /> {t("pd.sendShopify")}
                </Button>
                <Button onClick={pushWix} size="sm" variant="outline" className="rounded-full border-white/15" data-testid="push-wix-btn">
                  <Storefront className="mr-1" /> {t("pd.sendWix")}
                </Button>
                {wixLinked && (
                  <Button onClick={pushWixConnected} size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="push-wix-connected-btn">
                    <Storefront className="mr-1" /> Enviar a Wix (conectada)
                  </Button>
                )}
              </div>
              <Link to="/dashboard/integrations" className="inline-flex items-center gap-1 text-xs text-gold hover:underline" data-testid="integration-guide-link">
                <ArrowSquareOut size={12} /> {t("pd.fullGuide")}
              </Link>
            </div>
          </div>
        </div>

        {/* Right: config */}
        <div className="space-y-6">
          {/* Materials */}
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]" data-testid="materials-card">
            <h3 className="font-semibold mb-1">{t("pd.materialsTitle")}</h3>
            <p className="text-xs text-neutral-500 mb-3">{t("pd.materialsNote")}</p>
            <div className="mb-3">
              <Label className="text-xs">{t("pd.materialFamily")}</Label>
              <select value={matFamily()} onChange={(e) => setMaterialFamily(e.target.value)} data-testid="material-family-select"
                className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                {FAMILY_ORDER.map((f) => (<option key={f} value={f}>{MATERIAL_FAMILIES[f].label}</option>))}
              </select>
            </div>
            {matFamily() === "metal" && (
              <div className="flex items-end gap-2 mb-3 bg-white/5 rounded-lg p-3 flex-wrap">
                <div>
                  <Label className="text-xs">{t("pd.pieceWeight")}</Label>
                  <Input type="number" step="0.1" value={p.dwt ?? 0} onChange={(e) => setField("dwt", parseFloat(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/15 h-9 w-32" data-testid="dwt-input" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              {(MATERIAL_PRESETS[matFamily()] || []).map((m) => {
                const sel = (p.metals || []).find((x) => x.id === m.id);
                const allowMarket = matFamily() === "metal";
                return (
                  <div key={m.id} className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => toggleMetal(m)} data-testid={`metal-toggle-${m.id}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm flex-1 min-w-[140px] ${sel ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/25"}`}>
                      <span className="w-4 h-4 rounded-full border border-white/20" style={{ background: m.hex }} /> {m.name}
                      {sel && <Check size={14} className="ml-auto text-gold" />}
                    </button>
                    {sel && (
                      <>
                        {allowMarket && (
                          <label className="flex items-center gap-1.5 text-[11px] text-neutral-400 cursor-pointer">
                            <input type="checkbox" checked={!!sel.market} onChange={(e) => setMetalMarket(m.id, e.target.checked)} data-testid={`metal-market-${m.id}`} /> {t("pd.marketPrice")}
                          </label>
                        )}
                        {allowMarket && sel.market ? (
                          <div className="relative w-24">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 text-[10px]">factor</span>
                            <Input type="number" step="1" value={sel.factor ?? 0} onChange={(e) => setMetalFactor(m.id, e.target.value)} className="pl-10 bg-white/5 border-white/15 h-9" data-testid={`metal-factor-${m.id}`} />
                          </div>
                        ) : (
                          <div className="relative w-28">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">$</span>
                            <Input type="number" value={sel.price ?? 0} onChange={(e) => setMetalPrice(m.id, e.target.value)} className="pl-6 bg-white/5 border-white/15 h-9" data-testid={`metal-price-${m.id}`} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {(p.metals || []).filter((m) => m.custom && (m.family || "metal") === matFamily()).map((m) => (
                <div key={m.id} className="flex items-center gap-2 flex-wrap" data-testid={`custom-color-${m.id}`}>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gold/40 bg-gold/5 text-sm flex-1 min-w-[140px]">
                    <span className="w-4 h-4 rounded-full border border-white/20" style={{ background: m.hex }} /> {m.name}
                    <span className="ml-auto text-[10px] text-neutral-500">{t("pd.customTag")}</span>
                  </div>
                  <div className="relative w-28">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">$</span>
                    <Input type="number" value={m.price ?? 0} onChange={(e) => setMetalPrice(m.id, e.target.value)} className="pl-6 bg-white/5 border-white/15 h-9" data-testid={`custom-price-${m.id}`} />
                  </div>
                  <button onClick={() => removeMaterial(m.id)} className="text-neutral-400 hover:text-red-400" data-testid={`custom-remove-${m.id}`}><Trash size={16} /></button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1 flex-wrap" data-testid="add-custom-color-row">
                <input type="color" value={colorHex} onChange={(e) => setColorHex(e.target.value)} className="w-9 h-9 rounded border border-white/15 bg-transparent cursor-pointer" data-testid="custom-color-hex" />
                <Input value={colorName} onChange={(e) => setColorName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomColor(); } }} placeholder={t("pd.addColorPlaceholder")} className="bg-white/5 border-white/15 h-9 flex-1 min-w-[140px]" data-testid="custom-color-name" />
                <Button size="sm" variant="outline" className="rounded-lg border-white/15 h-9" onClick={addCustomColor} data-testid="custom-color-add"><Plus size={14} className="mr-1" /> {t("pd.addColor")}</Button>
              </div>
            </div>
          </div>

          {/* Stone & enamel colors */}
          {(p.klass || "joyeria") === "joyeria" && <PaletteEditor />}

          {/* Logo color (customer) — non-jewelry */}
          {(p.material_family || "metal") !== "metal" && (
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]" data-testid="logo-color-card">
              <h3 className="font-semibold mb-1">{t("pd.logoColorTitle")}</h3>
              <p className="text-xs text-neutral-500 mb-3">{t("pd.logoColorNote")}</p>
              <label className="flex items-center justify-between gap-2 text-sm">
                <span className="text-neutral-300">{t("pd.logoColorAllow")}</span>
                <Switch checked={!!p.logo_customer_color} onCheckedChange={(v) => setField("logo_customer_color", v)} data-testid="logo-color-switch" />
              </label>
              {p.logo_customer_color && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t("pd.logoColorMode")}</Label>
                    <select value={p.logo_color_mode || "global"} onChange={(e) => setField("logo_color_mode", e.target.value)}
                      data-testid="logo-color-mode-select" className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                      <option value="global">{t("pd.logoColorModeGlobal")}</option>
                      <option value="per_logo">{t("pd.logoColorModePer")}</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">{t("pd.logoColorDefault")}</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input type="color" value={p.logo_default_color || "#333333"} onChange={(e) => setField("logo_default_color", e.target.value)} className="w-9 h-9 rounded border border-white/15 bg-transparent cursor-pointer" data-testid="logo-color-default-input" />
                      <span className="text-xs text-neutral-500">{p.logo_default_color || "#333333"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Size */}
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{t("pd.sizeTitle")}</h3>
              <Switch checked={!!p.size_enabled} onCheckedChange={(v) => setField("size_enabled", v)} data-testid="size-switch" />
            </div>
            {p.size_enabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-sm text-neutral-400">{t("pd.sizeUnit")}</Label>
                  <select value={p.size_unit || ""} onChange={(e) => setField("size_unit", e.target.value)}
                    data-testid="size-unit-select" className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                    {SIZE_UNITS.map((u) => (<option key={u.key} value={u.key}>{u.label}</option>))}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-neutral-300">
                  <Switch checked={!!p.size_free_text} onCheckedChange={(v) => setField("size_free_text", v)} data-testid="size-freetext-switch" />
                  {t("pd.sizeFree")}
                </label>
                {!p.size_free_text && (
                  <div>
                    <Label className="text-sm text-neutral-400">{t("pd.sizesAvailable")}</Label>
                    <div className="flex flex-wrap gap-1.5 my-2">
                      <span className="text-[11px] text-neutral-500 self-center">{t("pd.sizePresets")}:</span>
                      {[["XS,S,M,L,XL,XXL", t("pd.sizePresetClothing")], ["5,6,7,8,9,10,11,12", t("pd.sizePresetRing")], ["28,30,32,34,36,38", t("pd.sizePresetWaist")]].map(([val, lbl]) => (
                        <button key={lbl} type="button" onClick={() => setField("size_options", val.split(",").map((s) => s.trim()))} className="text-[11px] px-2 py-1 rounded-full border border-white/15 text-neutral-300 hover:border-gold hover:text-gold" data-testid={`size-preset-${lbl}`}>{lbl}</button>
                      ))}
                    </div>
                    <Input value={(p.size_options || []).join(", ")} onChange={(e) => setField("size_options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="mt-1 bg-white/5 border-white/15" data-testid="size-options-input" placeholder="S, M, L, XL  ·  o  30, 40, 50 cm" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dimensions (furniture / 3D models) — informative */}
          {["mueble", "modelo3d"].includes(p.klass || "") && (
            <div className="border border-white/10 rounded-xl p-5 bg-[#121212]" data-testid="dimensions-card">
              <h3 className="font-semibold mb-1">{t("pd.dimTitle")}</h3>
              <p className="text-xs text-neutral-500 mb-3">{t("pd.dimNote")}</p>
              <div className="grid grid-cols-3 gap-2">
                {[["height", t("pd.dimHeight")], ["width", t("pd.dimWidth")], ["depth", t("pd.dimDepth")]].map(([k, lbl]) => (
                  <div key={k}>
                    <Label className="text-xs">{lbl}</Label>
                    <Input type="number" step="0.1" value={(p.dimensions || {})[k] ?? ""} onChange={(e) => setField("dimensions", { ...(p.dimensions || {}), [k]: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`dim-${k}-input`} />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Label className="text-xs">{t("pd.dimUnit")}</Label>
                <select value={(p.dimensions || {}).unit || "cm"} onChange={(e) => setField("dimensions", { ...(p.dimensions || {}), unit: e.target.value })} data-testid="dim-unit-select" className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                  {["cm", "mm", "in", "ft"].map((u) => (<option key={u} value={u}>{u}</option>))}
                </select>
              </div>
            </div>
          )}

          {/* Engraving (not for clothing/fabric) */}
          {(p.material_family || "metal") !== "fabric" && (
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{t("pd.engravingTitle")}</h3>
              <Switch checked={!!p.engraving_enabled} onCheckedChange={(v) => setField("engraving_enabled", v)} data-testid="engraving-switch" />
            </div>
            {p.engraving_enabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-sm text-neutral-400">{t("pd.engravingMax")}</Label>
                  <Input type="number" value={p.engraving_max_length || 30} onChange={(e) => setField("engraving_max_length", parseInt(e.target.value) || 30)} className="mt-1 bg-white/5 border-white/15 h-9 w-28" />
                </div>
                <div>
                  <Label className="text-sm text-neutral-400">{t("pd.engravingPrice")}</Label>
                  <Input type="number" step="0.01" min="0" value={p.engraving_price || 0} onChange={(e) => setField("engraving_price", parseFloat(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/15 h-9 w-40" data-testid="engraving-price-input" />
                  <p className="text-[11px] text-neutral-500 mt-1">{t("pd.engravingPriceNote")}</p>
                </div>
                <div>
                  <Label className="text-sm text-neutral-400">{t("pd.engravingFonts")}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {ENGRAVING_FONTS.map((f) => {
                      const sel = (p.engraving_fonts || []).includes(f.id);
                      return (
                        <button key={f.id} data-testid={`font-opt-${f.id}`}
                          onClick={() => {
                            const cur = p.engraving_fonts || [];
                            if (sel) setField("engraving_fonts", cur.filter((x) => x !== f.id));
                            else if (cur.length < 2) setField("engraving_fonts", [...cur, f.id]);
                            else toast.error(t("pd.t_max2fonts"));
                          }}
                          className={`px-3 py-2 rounded-lg border text-left ${sel ? "border-gold bg-gold/10" : "border-white/15 bg-white/5 hover:border-white/30"}`}>
                          <div style={{ fontFamily: f.css }} className="text-base leading-none">Aa Bb</div>
                          <div className="text-[10px] text-neutral-400 mt-1">{f.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-neutral-400">{t("pd.engravingProject")} <span className="text-neutral-600">{t("pd.engravingProjectNote")}</span></span>
                  <Switch checked={!!p.engraving_project_3d} onCheckedChange={(v) => setField("engraving_project_3d", v)} data-testid="engraving-project3d-switch" />
                </label>
                <label className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-neutral-400">Invertir grabado <span className="text-neutral-600">(si el texto aparece al revés en la vista del cliente)</span></span>
                  <Switch checked={!!p.engraving_flip} onCheckedChange={(v) => setField("engraving_flip", v)} data-testid="engraving-flip-switch" />
                </label>
              </div>
            )}
          </div>
          )}

          {/* Price */}
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
            <h3 className="font-semibold mb-1">{t("pd.priceTitle")}</h3>
            <p className="text-xs text-neutral-500 mb-3">{t("pd.priceNote")}</p>
            <div className="relative w-40">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
              <Input type="number" value={p.price ?? 0} onChange={(e) => setField("price", parseFloat(e.target.value) || 0)} className="pl-7 bg-white/5 border-white/15 h-9" data-testid="product-price-input" />
            </div>
          </div>

          {/* Extra options: enamel, gold plating, collections */}
          <div className="border border-white/10 rounded-xl p-5 bg-[#121212] space-y-5">
            <h3 className="font-semibold">{t("pd.extraOptions")}</h3>
            <div className="border-b border-white/10 pb-4">
              <div className="font-medium text-sm mb-1">{t("pd.productClass")}</div>
              <div className="text-xs text-neutral-500 mb-2">{t("pd.productClassNote")}</div>
              <div className="grid grid-cols-2 gap-2">
                <select value={p.klass || "joyeria"} onChange={(e) => { const v = e.target.value; const fam = CLASS_DEFAULT_FAMILY[v] || "metal"; const patch = { klass: v, subtype: v === "joyeria" ? p.subtype : null, ...familyPatch(fam) }; setP({ ...p, ...patch }); save(patch, true); }}
                  data-testid="product-class-select" className="bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                  {PRODUCT_CLASSES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
                </select>
                {(p.klass || "joyeria") === "joyeria" && (
                  <select value={p.subtype || ""} onChange={(e) => { const v = e.target.value || null; setField("subtype", v); save({ subtype: v }, true); }}
                    data-testid="product-subtype-select" className="bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                    <option value="">{t("pd.subtypePlaceholder")}</option>
                    {JEWELRY_SUBTYPES.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                  </select>
                )}
              </div>
            </div>
            <div className="border-b border-white/10 pb-4">
              <div className="font-medium text-sm mb-1">{t("pd.productCategory")}</div>
              <div className="text-xs text-neutral-500 mb-2">{t("pd.productCategoryNote")}</div>
              <select value={p.category_id || ""} onChange={(e) => {
                  const v = e.target.value || null;
                  const cat = categories.find((c) => c.id === v);
                  const patch = { category_id: v };
                  if (cat && cat.material_family) Object.assign(patch, familyPatch(cat.material_family));
                  setP({ ...p, ...patch }); save(patch, true);
                }}
                data-testid="product-category-select" className="w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                <option value="">{t("pd.noCategory")}</option>
                {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
              {(() => {
                const cat = categories.find((c) => c.id === p.category_id);
                if (!cat || !(cat.subcategories || []).length) return null;
                return (
                  <div className="mt-2">
                    <div className="text-xs text-neutral-500 mb-1">{t("pd.subcategory")}</div>
                    <select value={p.subcategory || ""} onChange={(e) => { const v = e.target.value || null; setField("subcategory", v); save({ subcategory: v }, true); }}
                      data-testid="product-subcategory-select" className="w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                      <option value="">—</option>
                      {cat.subcategories.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                );
              })()}
              <div className="mt-3">
                <div className="text-xs text-neutral-500 mb-1">Colección de la tienda (opcional)</div>
                <input value={p.subcategory || ""} onChange={(e) => setField("subcategory", e.target.value)} onBlur={(e) => save({ subcategory: e.target.value || null }, true)}
                  placeholder="Ej: Anillos, Collares, Novia…" data-testid="product-collection-input"
                  className="w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200" />
                <div className="text-[11px] text-neutral-500 mt-1">Agrupa este producto en una colección de tu tienda. Activa el selector "Colección" en los botones y la página de colección.</div>
              </div>
            </div>
            {(p.material_family || "metal") === "metal" && (<>
            <div className="flex items-center justify-between">
              <div><div className="font-medium text-sm">{t("pd.enamelColor")}</div><div className="text-xs text-neutral-500">{t("pd.enamelColorNote")}</div></div>
              <Switch checked={!!p.enamel_enabled} onCheckedChange={(v) => setField("enamel_enabled", v)} data-testid="enamel-switch" />
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between">
                <div><div className="font-medium text-sm">{t("pd.goldPlating")}</div><div className="text-xs text-neutral-500">{t("pd.goldPlatingNote")}</div></div>
                <Switch checked={!!p.gold_plating_enabled} onCheckedChange={(v) => setField("gold_plating_enabled", v)} data-testid="goldplating-switch" />
              </div>
              {p.gold_plating_enabled && (
                <div className="relative w-40 mt-3">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">+$</span>
                  <Input type="number" value={p.gold_plating_price ?? 0} onChange={(e) => setField("gold_plating_price", parseFloat(e.target.value) || 0)} className="pl-8 bg-white/5 border-white/15 h-9" data-testid="goldplating-price-input" />
                </div>
              )}
            </div>
            </>)}
            <div className="border-t border-white/10 pt-4">
              <div className="font-medium text-sm mb-1">{t("pd.collectionsForClient")}</div>
              <div className="text-xs text-neutral-500 mb-2">{t("pd.collectionsForClientNote")}</div>
              <div className="flex flex-wrap gap-2">
                {collections.length === 0 && <span className="text-xs text-neutral-500">{t("pd.noCollectionsYet")}</span>}
                {collections.map((c) => {
                  const on = (p.allowed_collection_ids || []).includes(c.id);
                  return (
                    <button key={c.id} onClick={() => setField("allowed_collection_ids", on ? (p.allowed_collection_ids || []).filter((x) => x !== c.id) : [...(p.allowed_collection_ids || []), c.id])}
                      data-testid={`allowed-col-${c.id}`}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs ${on ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-neutral-400 hover:border-white/25"}`}>
                      {on && <Check size={11} weight="bold" />} {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
            {(p.klass || "joyeria") === "joyeria" && p.subtype === "sortija_graduacion" && (
            <div className="border-t border-white/10 pt-4">
              <div className="font-medium text-sm mb-1">{t("pd.defaultUni")}</div>
              <div className="text-xs text-neutral-500 mb-2">{t("pd.defaultUniNote")}</div>
              <select value={p.default_collection_id || ""} onChange={(e) => { const v = e.target.value || null; setField("default_collection_id", v); save({ default_collection_id: v }, true); }}
                data-testid="default-collection-select" className="w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
                <option value="">{t("pd.defaultUniNone")}</option>
                {collections.filter((c) => (c.type || "university") === "university").map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            )}
          </div>

          {(p.catalog_type || "custom3d") === "custom3d" && <ZoneLogoRules
            parts={p.model_parts || []}
            metals={p.metals || []}
            onPartsChange={onPartsChange}
            uploadPrice={p.customer_upload_price}
            onUploadPriceChange={(v) => { setField("customer_upload_price", v); save({ customer_upload_price: v }, true); }}
          />}
        </div>
      </div>
    </div>
  );
}

function CatalogTypeCard({ p, save }) {
  const ct = p.catalog_type || "custom3d";
  const [variants, setVariants] = useState(p.variants || []);
  const inputCls = "bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200";
  const setType = (v) => save({ catalog_type: v }, true);
  const addRow = () => { const nv = [...variants, { label: "", price: Number(p.price || 0), stock: 0 }]; setVariants(nv); };
  const updRow = (i, k, val) => { const nv = variants.map((r, j) => j === i ? { ...r, [k]: (k === "label" ? val : Number(val)) } : r); setVariants(nv); };
  const delRow = (i) => { const nv = variants.filter((_, j) => j !== i); setVariants(nv); save({ variants: nv }, true); };
  const saveVariants = () => save({ variants, variant_name: p.variant_name || "Opción" }, false);
  return (
    <div className="mb-6 border border-white/10 rounded-xl p-5 bg-[#121212]" data-testid="catalog-type-card">
      <h3 className="font-semibold mb-3">Tipo de producto</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["custom3d", "Personalizable 3D"], ["simple", "Simple (sin 3D)"], ["digital", "Digital / descargable"]].map(([v, label]) => (
          <button key={v} onClick={() => setType(v)} data-testid={`catalog-type-${v}`}
            className={`px-3 py-1.5 rounded-full border text-sm ${ct === v ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-300"}`}>{label}</button>
        ))}
      </div>
      {ct !== "custom3d" && (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-neutral-400">Precio base ($)</label>
              <input type="number" defaultValue={p.price || 0} onBlur={(e) => save({ price: Number(e.target.value) }, true)} className={`${inputCls} w-full mt-1`} data-testid="catalog-price" /></div>
            <div><label className="text-xs text-neutral-400">Stock (vacío = ilimitado)</label>
              <input type="number" defaultValue={p.stock ?? ""} onBlur={(e) => save({ stock: e.target.value === "" ? null : Number(e.target.value), track_stock: e.target.value !== "" }, true)} className={`${inputCls} w-full mt-1`} data-testid="catalog-stock" /></div>
            <div><label className="text-xs text-neutral-400">Nombre de variante</label>
              <input defaultValue={p.variant_name || ""} placeholder="Talla / Color" onBlur={(e) => save({ variant_name: e.target.value }, true)} className={`${inputCls} w-full mt-1`} data-testid="catalog-variant-name" /></div>
          </div>
          {ct === "digital" && (
            <div><label className="text-xs text-neutral-400">Enlace de descarga (URL del archivo)</label>
              <input defaultValue={p.digital_url || ""} placeholder="https://..." onBlur={(e) => save({ digital_url: e.target.value.trim() }, true)} className={`${inputCls} w-full mt-1 font-mono`} data-testid="catalog-digital-url" /></div>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-neutral-400">Variantes (opcional) — cada una con su precio y stock</label>
              <button onClick={addRow} className="text-xs text-gold" data-testid="catalog-add-variant">+ Añadir variante</button>
            </div>
            <div className="space-y-2">
              {variants.map((r, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`catalog-variant-row-${i}`}>
                  <input value={r.label} onChange={(e) => updRow(i, "label", e.target.value)} placeholder="Etiqueta (S, Rojo…)" className={`${inputCls} flex-1`} />
                  <input type="number" value={r.price} onChange={(e) => updRow(i, "price", e.target.value)} placeholder="Precio" className={`${inputCls} w-24`} />
                  <input type="number" value={r.stock} onChange={(e) => updRow(i, "stock", e.target.value)} placeholder="Stock" className={`${inputCls} w-20`} />
                  <button onClick={() => delRow(i)} className="text-neutral-500 hover:text-red-400 text-xs px-2">✕</button>
                </div>
              ))}
            </div>
            {variants.length > 0 && <button onClick={saveVariants} className="mt-3 px-4 py-2 rounded-full bg-gold text-obsidian text-sm font-semibold" data-testid="catalog-save-variants">Guardar variantes</button>}
          </div>
          {ct === "simple" && <SimpleCustomCard p={p} save={save} />}
          <p className="text-[11px] text-neutral-500">Los productos sin 3D se compran directo desde tu tienda. Las imágenes se toman de las imágenes de referencia del producto.</p>
        </div>
      )}
    </div>
  );
}



const AREA_PRESETS = [
  { id: "center", label: "Central" },
  { id: "topleft", label: "Superior izquierda" },
  { id: "topright", label: "Superior derecha" },
  { id: "left", label: "Izquierda" },
  { id: "right", label: "Derecha" },
];

function SimpleCustomCard({ p, save }) {
  const [st, setSt] = useState({
    simple_custom: !!p.simple_custom,
    stone_sets: Number(p.stone_sets || 0),
    simple_enamel: !!p.simple_enamel,
    logo_areas: p.logo_areas || [],
    customer_upload_price: p.customer_upload_price || 0,
  });
  const [logos, setLogos] = useState([]);
  const [newArea, setNewArea] = useState("");
  const [openArea, setOpenArea] = useState(null);
  useEffect(() => { api.get("/logos").then((r) => setLogos((r.data || []).filter((l) => l.url))).catch(() => {}); }, []);
  const upd = (patch) => { setSt((s) => ({ ...s, ...patch })); save(patch, true); };
  const inputCls = "bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200";

  const areas = st.logo_areas;
  const hasArea = (id) => areas.some((a) => a.id === id);
  const toggleArea = (a) => upd({ logo_areas: hasArea(a.id) ? areas.filter((x) => x.id !== a.id) : [...areas, { ...a, allowed_logo_ids: [], customer_upload_enabled: false }] });
  const addCustomArea = () => {
    const label = newArea.trim(); if (!label) return;
    upd({ logo_areas: [...areas, { id: "a_" + Math.random().toString(36).slice(2, 7), label, allowed_logo_ids: [], customer_upload_enabled: false }] });
    setNewArea("");
  };
  const patchArea = (id, patch) => upd({ logo_areas: areas.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const toggleLogo = (a, lid) => {
    const cur = a.allowed_logo_ids || [];
    patchArea(a.id, { allowed_logo_ids: cur.includes(lid) ? cur.filter((x) => x !== lid) : [...cur, lid] });
  };

  return (
    <div className="mt-4 border border-gold/25 rounded-xl p-5 bg-gold/[0.03]" data-testid="simple-custom-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">Personalizar sin 3D</h3>
          <p className="text-[11px] text-neutral-500">Usa el mismo personalizador del 3D, mostrando la foto principal del producto. Material, grabado y talla se configuran en sus secciones de siempre.</p>
        </div>
        <Switch checked={st.simple_custom} onCheckedChange={(v) => upd({ simple_custom: v })} data-testid="simple-custom-switch" />
      </div>

      {st.simple_custom && (
        <div className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-white/10 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
              <span className="text-sm text-neutral-300">Sets de piedras</span>
              <select value={st.stone_sets} onChange={(e) => upd({ stone_sets: Number(e.target.value) })} className={inputCls} data-testid="simple-stone-sets-select">
                {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n === 0 ? "Sin piedras" : n}</option>)}
              </select>
            </div>
            <label className="border border-white/10 rounded-lg px-3 py-2 flex items-center justify-between gap-2 text-sm text-neutral-300">
              <span>Color de esmalte</span>
              <Switch checked={st.simple_enamel} onCheckedChange={(v) => upd({ simple_enamel: v })} data-testid="simple-enamel-switch" />
            </label>
          </div>

          <div className="border border-white/10 rounded-lg p-3">
            <Label className="text-xs text-neutral-400">Zonas de logo</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {AREA_PRESETS.map((a) => (
                <button key={a.id} onClick={() => toggleArea(a)} data-testid={`simple-area-${a.id}`}
                  className={`px-3 py-1.5 rounded-full border text-xs ${hasArea(a.id) ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-300"}`}>{a.label}</button>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input value={newArea} onChange={(e) => setNewArea(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustomArea()} placeholder="Otra zona (ej. Interior)" className={`${inputCls} flex-1`} data-testid="simple-area-input" />
              <button onClick={addCustomArea} className="px-3 py-1.5 rounded-full border border-white/15 text-neutral-300 text-xs" data-testid="simple-area-add">+ Zona</button>
            </div>

            <div className="mt-3 space-y-2">
              {areas.map((a) => {
                const allowed = a.allowed_logo_ids || [];
                const open = openArea === a.id;
                return (
                  <div key={a.id} className="border border-white/10 rounded-lg" data-testid={`simple-zone-${a.id}`}>
                    <div className="flex items-center justify-between px-3 py-2">
                      <button onClick={() => setOpenArea(open ? null : a.id)} className="text-sm text-neutral-200 text-left flex-1" data-testid={`simple-zone-toggle-${a.id}`}>
                        {a.label} <span className="text-[11px] text-neutral-500 ml-2">{allowed.length ? `${allowed.length} logos` : "Todos los logos"}{a.customer_upload_enabled ? " · sube el suyo" : ""}</span>
                      </button>
                      <button onClick={() => upd({ logo_areas: areas.filter((x) => x.id !== a.id) })} className="text-neutral-500 hover:text-red-400 text-xs px-1" data-testid={`simple-zone-remove-${a.id}`}><Trash size={14} /></button>
                    </div>
                    {open && (
                      <div className="px-3 pb-3 border-t border-white/10 pt-3">
                        <p className="text-[11px] text-neutral-500 mb-2">Marca los logos permitidos. Si no marcas ninguno, se ofrecen todos los de tu biblioteca.</p>
                        <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                          {logos.map((l) => {
                            const on = allowed.includes(l.id);
                            return (
                              <button key={l.id} onClick={() => toggleLogo(a, l.id)} title={l.name} data-testid={`simple-zone-${a.id}-logo-${l.id}`}
                                className={`aspect-square rounded-lg border overflow-hidden ${on ? "border-gold ring-1 ring-gold" : "border-white/15"}`}>
                                <img src={assetUrl(l.url)} alt={l.name} className="w-full h-full object-contain bg-white/5 p-1" />
                              </button>
                            );
                          })}
                          {logos.length === 0 && <span className="col-span-full text-[11px] text-neutral-500">Sube logos en la sección "Logos".</span>}
                        </div>
                        <label className="flex items-center justify-between mt-3 text-xs text-neutral-400">
                          <span>Permitir que el cliente suba su propio logo aquí</span>
                          <Switch checked={!!a.customer_upload_enabled} onCheckedChange={(v) => patchArea(a.id, { customer_upload_enabled: v })} data-testid={`simple-zone-upload-${a.id}`} />
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {areas.some((a) => a.customer_upload_enabled) && (
              <div className="mt-3"><label className="text-xs text-neutral-400">Cargo extra si el cliente sube su logo ($)</label>
                <input type="number" step="0.01" defaultValue={st.customer_upload_price} onBlur={(e) => upd({ customer_upload_price: parseFloat(e.target.value) || 0 })} className={`${inputCls} w-40 mt-1 block`} data-testid="simple-upload-price" /></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
